'use client';

import React, { useState } from 'react';
import styled from 'styled-components';
import { COLORS } from '@/constants/colors';
import { SPACING } from '@/constants/spacing';
import { BREAKPOINTS } from '@/constants/breakpoints';

interface TrustpilotWidgetProps {
  enabled?: boolean;
}

/**
 * Professional Trustpilot Floating Badge
 * Clean design that works with or without reviews
 */
const TrustpilotWidget: React.FC<TrustpilotWidgetProps> = ({
  enabled = false,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  if (!enabled) {
    return null;
  }

  return (
    <WidgetContainer>
      {!isExpanded ? (
        // Compact floating button
        <CompactButton onClick={() => setIsExpanded(true)}>
          <StarContainer>
            <Star>★</Star>
            <Star>★</Star>
            <Star>★</Star>
            <Star>★</Star>
            <Star>★</Star>
          </StarContainer>
          <ButtonText>
            <BrandName>Trustpilot</BrandName>
            <SubText>See our reviews</SubText>
          </ButtonText>
        </CompactButton>
      ) : (
        // Expanded card
        <ExpandedCard>
          <CardHeader>
            <LogoSection>
              <TrustpilotText>Trustpilot</TrustpilotText>
              <StarsRow>
                <GreenStar>★</GreenStar>
                <GreenStar>★</GreenStar>
                <GreenStar>★</GreenStar>
                <GreenStar>★</GreenStar>
                <GreenStar>★</GreenStar>
              </StarsRow>
            </LogoSection>
            <CloseBtn onClick={() => setIsExpanded(false)}>✕</CloseBtn>
          </CardHeader>

          <CardBody>
            <ReviewMessage>
              <MessageTitle>Help us grow!</MessageTitle>
              <MessageText>
                We'd love to hear about your experience with Green Filter USA.
                Your feedback helps us serve you better.
              </MessageText>
            </ReviewMessage>

            <CTAButton
              href="https://www.trustpilot.com/review/greenfilter.com"
              target="_blank"
              rel="noopener noreferrer"
            >
              Write a Review
            </CTAButton>

            <ViewAllButton
              href="https://www.trustpilot.com/review/greenfilter.com"
              target="_blank"
              rel="noopener noreferrer"
            >
              View on Trustpilot →
            </ViewAllButton>
          </CardBody>
        </ExpandedCard>
      )}
    </WidgetContainer>
  );
};

export default TrustpilotWidget;

// Styled Components
const WidgetContainer = styled.div`
  position: fixed;
  bottom: 20px;
  right: 24px;
  z-index: 99995;

  @media (max-width: ${BREAKPOINTS.tablet}) {
    bottom: 20px;
    right: 16px;
  }

  @media (max-width: ${BREAKPOINTS.mobile}) {
    bottom: 20px;
    right: 12px;
  }
`;

const CompactButton = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  background: #00b67a;
  border: none;
  border-radius: 100px;
  padding: 12px 20px 12px 16px;
  cursor: pointer;
  box-shadow: 0 4px 20px rgba(0, 182, 122, 0.25);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 24px rgba(0, 182, 122, 0.35);
  }

  @media (max-width: ${BREAKPOINTS.mobile}) {
    padding: 10px 16px 10px 14px;
    gap: 10px;
  }
`;

const StarContainer = styled.div`
  display: flex;
  gap: 2px;
`;

const Star = styled.span`
  color: ${COLORS.textPrimary};
  font-size: 16px;
  line-height: 1;

  @media (max-width: ${BREAKPOINTS.mobile}) {
    font-size: 14px;
  }
`;

const ButtonText = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
`;

const BrandName = styled.span`
  color: ${COLORS.textPrimary};
  font-size: 14px;
  font-weight: 700;
  line-height: 1;
  font-family: 'Lato', sans-serif;

  @media (max-width: ${BREAKPOINTS.mobile}) {
    font-size: 13px;
  }
`;

const SubText = styled.span`
  color: ${COLORS.textPrimary};
  font-size: 11px;
  font-weight: 400;
  line-height: 1;
  font-family: 'Lato', sans-serif;

  @media (max-width: ${BREAKPOINTS.mobile}) {
    font-size: 10px;
  }
`;

const ExpandedCard = styled.div`
  background: ${COLORS.white};
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
  overflow: hidden;
  width: 340px;
  animation: slideUp 0.3s ease;

  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @media (max-width: ${BREAKPOINTS.tablet}) {
    width: 320px;
  }

  @media (max-width: ${BREAKPOINTS.mobile}) {
    width: 300px;
  }
`;

const CardHeader = styled.div`
  background: #00b67a;
  padding: 16px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const LogoSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const TrustpilotText = styled.div`
  color: ${COLORS.textPrimary};
  font-size: 18px;
  font-weight: 700;
  font-family: 'Lato', sans-serif;
  letter-spacing: 0.3px;
`;

const StarsRow = styled.div`
  display: flex;
  gap: 3px;
`;

const GreenStar = styled.span`
  color: ${COLORS.textPrimary};
  font-size: 18px;
  line-height: 1;
`;

const CloseBtn = styled.button`
  background: rgba(0, 0, 0, 0.08);
  border: none;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${COLORS.textPrimary};
  font-size: 18px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(0, 0, 0, 0.15);
    transform: rotate(90deg);
  }
`;

const CardBody = styled.div`
  padding: 20px;
  background: ${COLORS.white};
`;

const ReviewMessage = styled.div`
  text-align: center;
  margin-bottom: 18px;
`;

const MessageTitle = styled.h4`
  color: ${COLORS.textPrimary};
  font-size: 18px;
  font-weight: 700;
  font-family: 'Lato', sans-serif;
  margin: 0 0 8px 0;

  @media (max-width: ${BREAKPOINTS.mobile}) {
    font-size: 16px;
  }
`;

const MessageText = styled.p`
  color: ${COLORS.textSecondary};
  font-size: 14px;
  font-family: 'Lato', sans-serif;
  line-height: 1.5;
  margin: 0;

  @media (max-width: ${BREAKPOINTS.mobile}) {
    font-size: 13px;
  }
`;

const CTAButton = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  padding: 14px 20px;
  background: #00b67a;
  color: ${COLORS.textPrimary};
  text-decoration: none;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 600;
  font-family: 'Lato', sans-serif;
  transition: all 0.2s ease;
  margin-bottom: 10px;

  &:hover {
    background: #009963;
    transform: translateY(-1px);
    box-shadow: 0 4px 16px rgba(0, 182, 122, 0.25);
  }

  &:active {
    transform: translateY(0);
  }

  @media (max-width: ${BREAKPOINTS.mobile}) {
    padding: 12px 18px;
    font-size: 14px;
  }
`;

const ViewAllButton = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  padding: 10px 16px;
  background: transparent;
  color: ${COLORS.textPrimary};
  text-decoration: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  font-family: 'Lato', sans-serif;
  transition: all 0.2s ease;
  border: 2px solid #00b67a;

  &:hover {
    background: rgba(0, 182, 122, 0.05);
  }

  @media (max-width: ${BREAKPOINTS.mobile}) {
    padding: 9px 14px;
    font-size: 13px;
  }
`;
